import React, { useState } from "react";
import {
  FaArrowRight,
  FaCircle,
  FaCheckCircle,
} from "react-icons/fa";
import type { PackageOption } from "../../utils/packageEnquiry";
import { resolvePackageIcon } from "./packageIconRegistry";

export type PackageEnquiryDraft = {
  selectedPackage: PackageOption;
  travellers: string;
  travelMonth: string;
  notes: string;
};

type PackagesSectionProps = {
  destinationName: string;
  packages: PackageOption[];
  onProceedToEnquiry: (draft: PackageEnquiryDraft) => void;
  onViewPackage?: (selectedPackage: PackageOption) => void;
};

type PackageFeatureItem = {
  iconName?: string;
  description: string;
};

const getIcon = (iconName?: string) => {
  const IconComponent = resolvePackageIcon(iconName) || FaCircle;
  return <IconComponent />;
};

const getPackageCardFeatures = (pkg: PackageOption): PackageFeatureItem[] => {
  const mappedFeatures = (pkg.features || [])
    .map((feature) => ({
      iconName: feature.iconName?.trim() || undefined,
      description: feature.description?.trim() || "",
    }))
    .filter((feature) => feature.description);
  if (mappedFeatures.length > 0) return mappedFeatures;
  return [
    { iconName: "clock", description: pkg.duration?.trim() || "" },
    { iconName: "suitcase-rolling", description: pkg.idealFor?.trim() || "" },
  ].filter((feature) => feature.description);
};

type InclusionItem = string | { iconName: string; description: string };

const getInclusionDescription = (item: InclusionItem) => {
  if (typeof item === "string") return item;
  return item.description || "";
};

const getInclusionIconName = (item: InclusionItem) => {
  if (typeof item === "string") return "";
  return item.iconName || "";
};

const getRegistryIcon = (iconName?: string | null) => {
  const IconComponent = resolvePackageIcon(iconName) || FaCircle;
  return <IconComponent />;
};

const PackagesSection: React.FC<PackagesSectionProps> = ({
  destinationName,
  packages,
  onProceedToEnquiry,
  onViewPackage,
}) => {
  const [selectedPackageId, setSelectedPackageId] = useState(
    () => packages[0]?.id ?? "",
  );
  const [enquiryDetails, setEnquiryDetails] = useState({
    travellers: "2 Adults",
    travelMonth: "",
    notes: "",
  });
  const selectedPackage =
    packages.find((pkg) => pkg.id === selectedPackageId) ?? packages[0] ?? null;

  const count = packages.length;
  const gridCols =
    count <= 2 ? "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto" :
    count === 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" :
    count === 5 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" :
    "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;

    setEnquiryDetails((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedPackage) {
      return;
    }

    onProceedToEnquiry({
      selectedPackage,
      travellers: enquiryDetails.travellers,
      travelMonth: enquiryDetails.travelMonth,
      notes: enquiryDetails.notes,
    });
  };

  return (
    <section className="bg-white py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-brand-pink font-semibold tracking-wider uppercase text-sm block mb-2">
            Curated Packages
          </span>

          <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark mb-4">
            Choose Your {destinationName} Package
          </h2>

          <p className="text-gray-500 max-w-2xl mx-auto">
            Pick a package to review the summary. Use View Package to open the package page or continue to enquiry from below.
          </p>
        </div>

        <div className={`grid ${gridCols} gap-6 mb-10`}>
          {packages.map((pkg) => {
            const isSelected = pkg.id === selectedPackageId;
            const cardFeatures = getPackageCardFeatures(pkg);

            return (
              <div
                key={pkg.id}
                onClick={() => setSelectedPackageId(pkg.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setSelectedPackageId(pkg.id);
                  }
                }}
                role="button"
                tabIndex={0}
                className={`w-full max-w-[360px] mx-auto text-left rounded-3xl border transition-all shadow-sm flex flex-col h-full ${
                  isSelected
                    ? "border-brand-pink bg-brand-pink/5 shadow-lg shadow-brand-pink/10"
                    : "border-gray-200 bg-white hover:border-brand-pink/50 hover:shadow-lg"
                }`}
              >
                <div className="p-6 pb-4 flex flex-col">
                  <div className="mb-8 h-14">
                    <h3 className="font-heading text-center text-xl md:text-2xl text-brand-dark leading-none font-bold">
                      {pkg.title}
                    </h3>
                  </div>

                  <div className="mt-2 text-center">
                    <span className="inline-block rounded-full bg-brand-dark px-3 py-1.5 text-[11px] font-bold text-white">
                      {pkg.price}
                    </span>
                  </div>

                  <div className="mt-8 space-y-2 text-sm text-gray-600">
                    {cardFeatures.map((feature, index) => (
                      <div
                        key={`${feature.iconName || "feature"}-${feature.description}-${index}`}
                        className="flex items-center gap-2"
                      >
                        <span className="text-brand-pink flex-shrink-0" aria-hidden="true">
                          {getIcon(feature.iconName)}
                        </span>
                        <span>{feature.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                  <div className="px-6 pb-2 flex-grow">
                    <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                      Inclusions
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(pkg.inclusions || []).map((item, index) => {
                        const description = getInclusionDescription(item);
                        const iconName = getInclusionIconName(item);

                        return (
                          <span
                            key={description || index}
                            title={description}
                            aria-label={description}
                            className="inline-flex min-h-11 items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600 transition hover:border-brand-pink/30 hover:bg-brand-pink/5 hover:text-brand-pink"
                          >
                            {iconName && (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-pink/10 text-brand-pink flex-shrink-0">
                                {getRegistryIcon(iconName)}
                              </span>
                            )}
                            <span className="text-xs font-medium text-gray-600">
                              {description}
                            </span>
                          </span>
                        );
                      })}
                    </div>
                  </div>

                <div className="p-6 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (onViewPackage) {
                        onViewPackage(pkg);
                        return;
                      }
                      setSelectedPackageId(pkg.id);
                    }}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-2xl font-semibold transition-all ${
                      isSelected 
                        ? "bg-brand-pink text-white hover:bg-brand-pink/90" 
                        : "text-brand-pink hover:bg-brand-pink/5"
                    }`}
                  >
                    View Package
                    <FaArrowRight className="text-xs" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {selectedPackage && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-8">
            <div className="rounded-3xl bg-brand-dark p-8 text-white shadow-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink">
                Package Summary
              </p>

              <h3 className="mt-3 font-heading text-3xl">
                {selectedPackage.title}
              </h3>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">
                    Duration
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    {selectedPackage.duration}
                  </p>
                </div>

                <div className="rounded-2xl bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-wider text-gray-400">
                    Price Guide
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    {selectedPackage.price}
                  </p>
                </div>
              </div>

              <p className="mt-6 text-sm leading-7 text-gray-300">
                {selectedPackage.summary}
              </p>

              <div className="mt-8">
                <h4 className="text-lg font-semibold text-white mb-3">
                  Package Highlights
                </h4>
                <div className="space-y-3">
                  {selectedPackage.highlights.map((item) => (
                    <div key={item} className="flex items-start gap-3 text-sm text-gray-300">
                      <FaCheckCircle className="mt-1 text-brand-pink" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <h4 className="text-lg font-semibold text-white mb-3">
                  Inclusions
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedPackage.inclusions || []).map((item, index) => {
                    const description = getInclusionDescription(item);
                    const iconName = getInclusionIconName(item);

                    return (
                      <span
                        key={description || index}
                        title={description}
                        aria-label={description}
                        className="flex min-h-12 w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-gray-200 transition hover:bg-white/10 min-[420px]:w-auto"
                      >
                        {iconName && (
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-pink/15 text-brand-pink">
                            {getRegistryIcon(iconName)}
                          </span>
                        )}
                        <span className="min-w-0 whitespace-nowrap text-xs font-medium leading-tight text-gray-200 sm:text-sm">
                          {description}
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="rounded-3xl border border-gray-200 bg-brand-gray p-8 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink">
                Continue to Enquiry
              </p>

              <h3 className="mt-3 font-heading text-3xl text-brand-dark">
                Tell us about your trip
              </h3>

              <p className="mt-3 text-sm leading-6 text-gray-600">
                We will carry this package summary into the enquiry page so your selected package details are already included.
              </p>

              <div className="mt-8 space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-brand-dark">
                    Travellers
                  </label>
                  <input
                    type="text"
                    name="travellers"
                    value={enquiryDetails.travellers}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 2 Adults"
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-brand-dark">
                    Preferred Travel Month
                  </label>
                  <input
                    type="month"
                    name="travelMonth"
                    value={enquiryDetails.travelMonth}
                    onChange={handleChange}
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-brand-dark">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={enquiryDetails.notes}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Share room preference, honeymoon plans, celebration details, or special requests."
                    className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-brand-dark focus:outline-none focus:border-brand-pink transition-colors resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-brand-pink px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-pink/90"
              >
                Continue to Enquiry
                <FaArrowRight />
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};

export default PackagesSection;
